import { useState, useRef, useContext, useEffect, useCallback, Suspense } from "react";
import getAgGridHeight from "../../../../utils/commonFunctions/getAgGridHeight";
import { useNavigate } from "react-router-dom";
import { Button, IconButton, Tooltip } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { CustomPagination } from "../../../shared";
import { Pagination } from "@material-ui/lab";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { LazyCustomImageDialog, LazySnackbarAPI, LazyCustomDialog } from "../../../shared";
import { CellValueChangedEvent, ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { PaginationTypes } from "../../../../@types/pagination.types";
import { AuthContext, UIContext } from "../../../../contexts";
import { DialogProps } from "../../../../@types/dialogProps.types";
import { SnackbarProps } from "../../../../@types/snackbarAPI.types";
import { dateTimeZoneConverter } from "../../../../utils/commonFunctions/date";
import { CustomImageDialog } from "../../../../@types/components/dialogProps.types";
import { api } from "../../../../utils/api";
import getLimit from "../../../../utils/pagination/getLimit";
import generateImageDialogLink from "../../../../utils/commonFunctions/generateImageDialogLink";

import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";

import "./announcements.css";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { useEmployerRoute } from "../../../../hooks";
import useRouteParam from "../../../../hooks/useRouteParam";
import generateImageUrl from "../../../../utils/commonFunctions/imageUrl";
import { trackPromise } from "react-promise-tracker";
import { getPermissionsFromGroupOwner } from "../../../../utils/getPermissionsFromGroupOwner";

const Announcements = () => {
	const [announcements, setAnnouncements] = useState<any[]>([]);
	const [gridHeight, setGridHeight] = useState<string>("400px");
	const [announcementsFilterSchema, setAnnouncementsFilterSchema] = useState({
		opacity: { value: null, type: null },
		from_date: { value: null, type: null },
		to_date: { value: null, type: null },
		subject: { value: null, type: null },
		description: { value: null, type: null },
		text_align: { value: null, type: null }
	});
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");

	const { user, setUser } = useContext(AuthContext);

	// eslint-disable-next-line no-unused-vars
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	// eslint-disable-next-line no-unused-vars
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const agGridRef = useRef<any>(null);
	const [limit, setLimit] = useState<number>();
	const [pagination, setPagination] = useState<PaginationTypes>({
		currentPage: 1,
		pageCount: 11
	});
	const navigate = useNavigate();
	const { setDashboardHeader } = useContext(UIContext);
	const [imageDialogProps, setImageDialogProps] = useState<CustomImageDialog>({
		openDialog: false,
		image: undefined,
		handleCloseCallback: () =>
			setImageDialogProps(
				Object.assign({}, imageDialogProps, {
					openDialog: false
				})
			)
	});
	const [deleteDialogProps, setDeleteDialogProps] = useState<DialogProps>({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	//useMediaQuery
	const xl = useMediaQuery("(min-width:1600px)");
	const lg = useMediaQuery("(min-width:1200px)");
	const md = useMediaQuery("(min-width:800px)");
	const sm = useMediaQuery("(min-width:600px)");
	const xs = useMediaQuery("(min-width:400px)");
	const _limit = getLimit(xl, lg, md, sm, xs);

	// Functions for Pagination
	const handlePageChange = useCallback(
		(event: React.ChangeEvent<unknown>, page: number) => {
			setPagination(
				Object.assign({}, pagination, {
					currentPage: page
				})
			);
		},
		[pagination]
	);

	const announcementPermission =
		user && isEmployerRoute && queryGroupNumber
			? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))?.employer_settings_menu.announcements_sub
					.self
			: user
			? getPermissionsFromGroupOwner(user, 0)?.settings_menu.announcements_sub.self
			: null;

	const getPageCount = useCallback(async () => {
		if (limit) {
			const announcementCount = await api.announcements.countAnnouncements();
			const _pageCount = Math.ceil(announcementCount / limit);
			// console.log("Page Count", _pageCount);
			setPagination(
				Object.assign({}, pagination, {
					pageCount: _pageCount
				})
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [limit]);

	// GET Announcements with Pagination
	const getFilterdAnnouncements = useCallback(
		async (limit: number | null = _limit, _page: number | null = null /* USER_STATUS_LIMIT.xl */) => {
			const params = {
				page: 1,
				limit: 30000
			};

			try {
				// console.log(params);
				let response = await trackPromise(api.announcements.filterAnnouncements(params));
				// console.log("Response Announcements", response);
				if (queryGroupNumber && response) {
					response = response.filter((group: any) => group.group_number === queryGroupNumber);
					// console.log("filteredLinks 1", response, queryGroupNumber);
				}
				if (response) setAnnouncements([...response]);
			} catch (err) {
				// console.log("err", err);
			}
		},
		[_limit, queryGroupNumber]
	);

	const handleAgGridReady = (params: GridReadyEvent) => {
		// eslint-disable-next-line no-unused-vars
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, params.api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
	};

	const deleteAnnouncement = useCallback(
		async (_id: string) => {
			const _announcements = await trackPromise(api.announcements.deleteAnnouncementById(_id));
			if (_announcements) {
				setAnnouncements(Object.assign([], [..._announcements]));

				setSnackbarAPICallProps(
					Object.assign({}, snackbarAPICallProps, {
						open: true,
						message: "Successfully deleted announcement",
						severity: "success"
					})
				);
			}

			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[snackbarAPICallProps]
	);

	const handleDeleteClick = () => {
		const openDialog = true;
		const title = "Delete Confirmation";
		const content = "Are you sure you want to delete the Announcement ?";
		const actions = [
			{
				label: "Cancel",
				name: "cancel",
				callback: () => setDeleteDialogProps(Object.assign({}, deleteDialogProps, { openDialog: false }))
			},
			{
				label: "Yes",
				name: "accept",
				callback: () => {
					const focusedCell = agGridRef.current.api.getFocusedCell();
					const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
					const {
						data: { _id }
					} = row;
					deleteAnnouncement(_id);
					setDeleteDialogProps(Object.assign({}, deleteDialogProps, { openDialog: false }));
					//window.location.reload(); // This Reload is for re-rendering after delete
				}
			}
		];
		setDeleteDialogProps(
			Object.assign({}, deleteDialogProps, {
				openDialog,
				title,
				content,
				actions
			})
		);
	};

	const handleDeleteDialogClose = useCallback(() => {
		setDeleteDialogProps(Object.assign({}, deleteDialogProps, { openDialog: false }));
	}, [deleteDialogProps]);

	const handleEditClick = ({
		_isEmployerRoute,
		_queryGroupNumber
	}: {
		_isEmployerRoute: boolean | null;
		_queryGroupNumber: number | null;
	}) => {
		const focusedCell = agGridRef.current.api.getFocusedCell();
		const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
		const { data } = row;
		const pathname =
			_isEmployerRoute === true && _queryGroupNumber !== null
				? `/employer/settings/announcement/edit?id=${data._id}&&new=false&&gn=${_queryGroupNumber}`
				: `/settings/announcement/edit?id=${data._id}&&new=false`;
		navigate(pathname);
	};

	const handleAssignGroupClick = useCallback(
		({
			_isEmployerRoute,
			_queryGroupNumber
		}: {
			_isEmployerRoute: boolean | null;
			_queryGroupNumber: number | null;
		}) => {
			const focusedCell = agGridRef.current.api.getFocusedCell();
			const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
			const { data } = row;
			const pathname =
				_isEmployerRoute === true && _queryGroupNumber !== null
					? `/employer/settings/announcement/assign-groups?id=${data._id}&&gn=${_queryGroupNumber}`
					: `/settings/announcement/assign-groups?id=${data._id}`;
			navigate(pathname);
		},
		[navigate]
	);

	const handleAnnouncementActionRenderer = ({
		_isEmployerRoute,
		_queryGroupNumber,
		announcementPermission
	}: {
		_isEmployerRoute: boolean | null;
		_queryGroupNumber: number | null;
		announcementPermission: any;
	}) => {
		if (!announcementPermission) return null;
		return (
			<div>
				{announcementPermission.delete && (
					<Tooltip title="Delete" enterDelay={500} leaveDelay={200} arrow>
						<IconButton>
							<DeleteIcon onClick={handleDeleteClick} />
						</IconButton>
					</Tooltip>
				)}
				{announcementPermission.update && (
					<Tooltip title="Edit" enterDelay={500} leaveDelay={200} arrow>
						<IconButton>
							<EditIcon
								onClick={() =>
									handleEditClick({
										_isEmployerRoute,
										_queryGroupNumber
									})
								}
							/>
						</IconButton>
					</Tooltip>
				)}
				{announcementPermission.assign_location && (
					<Tooltip title="Assign Location" enterDelay={500} leaveDelay={200} arrow>
						<IconButton>
							<AssignmentTurnedInIcon
								onClick={() =>
									handleAssignGroupClick({
										_isEmployerRoute,
										_queryGroupNumber
									})
								}
							/>
						</IconButton>
					</Tooltip>
				)}
			</div>
		);
	};

	const handleAddAnnouncementClick = useCallback(() => {
		const pathname =
			isEmployerRoute === true && queryGroupNumber !== null
				? `/employer/settings/announcement/edit?new=true&&gn=${queryGroupNumber}`
				: "/settings/announcement/edit?new=true";
		navigate(pathname);
	}, [isEmployerRoute, navigate, queryGroupNumber]);

	const handleOpenImageModal = () => {
		const focusedCell = agGridRef.current.api.getFocusedCell();
		const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
		const {
			data: { image }
		} = row;
		setImageDialogProps(
			Object.assign({}, imageDialogProps, {
				openDialog: true,
				image: generateImageUrl(image)
				// image: `http://14.96.189.251:4000/api/v1/show-file/${image.filename}`
			})
		);
	};

	const handleViewImageActionRender = ({ value }: { value: any }) => {
		return (
			<div>
				{value ? (
					<IconButton onClick={handleOpenImageModal}>
						<VisibilityIcon />
					</IconButton>
				) : (
					<div style={{ marginLeft: 15 }}>N/A</div>
				)}
			</div>
		);
	};

	const handleDateCellRenderer = ({ value }: { value: string }) => {
		const date = dateTimeZoneConverter(value as string) as string;
		// console.log("date", date);
		return <div>{date}</div>;
	};

	const handleSubjectCellRenderer = useCallback(({ value }: { value: string }) => {
		return <div>{value === "" ? "(No Subject)" : value}</div>;
	}, []);

	const functionToBePassed = () => {
		// console.log("I am functionToBePassed");
	};

	// useEffect(() => {
	// 	setLimit(_limit);
	// 	getPageCount();
	// 	//getAnnouncements();
	// 	setDashboardHeader("Announcements");
	// }, [_limit, getPageCount, setDashboardHeader]);

	// useEffect(() => {
	// 	if (limit) {
	// 		getFilterdAnnouncements();
	// 	}
	// }, [pagination, limit, getFilterdAnnouncements]);

	useEffect(() => {
		// console.log("Announcements Schema: ", announcementsFilterSchema);
		setDashboardHeader("Announcements");
		getFilterdAnnouncements();
	}, [getFilterdAnnouncements, announcements.length, setDashboardHeader]);

	const { currentPage, pageCount } = pagination;

	useEffect(() => {
		const _gridHeight = getAgGridHeight(announcements.length);
		setGridHeight(_gridHeight);
	}, [announcements.length]);

	// console.log("announcements", announcements);

	return (
		<div className="announcements">
			<Suspense fallback={<div />}>
				<LazyCustomImageDialog dialogProps={imageDialogProps} />
			</Suspense>
			<Suspense fallback={<div />}>
				<LazyCustomDialog dialogProps={deleteDialogProps} handleClose={handleDeleteDialogClose} />
			</Suspense>
			<Suspense fallback={<div />}>
				<LazySnackbarAPI snackbarProps={snackbarAPICallProps} />
			</Suspense>
			{announcementPermission && announcementPermission.create && (
				<div className="action-container">
					<Button
						className="button-green margin-10px"
						variant="contained"
						onClick={handleAddAnnouncementClick}
					>
						<span className="button-label-with-icon">Add Announcement</span>
						<span>
							<AddIcon className="button-icon" />
						</span>
					</Button>
				</div>
			)}
			<div className="data-grid-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={announcements}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						animateRows
						rowDragManaged
						frameworkComponents={{
							handleAnnouncementActionRenderer: handleAnnouncementActionRenderer,
							handleViewImageActionRender: handleViewImageActionRender,
							handleDateCellRenderer: handleDateCellRenderer,
							handleSubjectCellRenderer: handleSubjectCellRenderer,
							schema: announcementsFilterSchema,
							setPayloadSchema: setAnnouncementsFilterSchema,
							functionToBePassed: functionToBePassed,
							setParentSchema: setAnnouncements
						}}
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						defaultColGroupDef={{ marryChildren: true }}
						rowSelection="multiple"
						onCellValueChanged={(params: CellValueChangedEvent) => {
							params.api.refreshCells({
								force: true,
								columns: ["action"]
							});
						}}
						enableCellChangeFlash={true}
					>
						<AgGridColumn
							field="subject.caption"
							headerName="Subject"
							rowDrag
							cellRenderer="handleSubjectCellRenderer"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							// floatingFilterComponent="customFloatingFilter"
							// filter="customFilter"
						></AgGridColumn>
						<AgGridColumn
							field="from_date"
							headerName="From Date"
							cellRenderer="handleDateCellRenderer"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							// floatingFilterComponent="customFloatingFilter"
							// filter="customFilter"
						></AgGridColumn>
						<AgGridColumn
							field="to_date"
							headerName="To Date"
							cellRenderer="handleDateCellRenderer"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							// floatingFilterComponent="customFloatingFilter"
							// filter="customFilter"
						></AgGridColumn>
						<AgGridColumn
							field="image"
							filter={false}
							cellRenderer="handleViewImageActionRender"
						></AgGridColumn>
						{announcementPermission &&
							(announcementPermission.delete ||
								announcementPermission.update ||
								announcementPermission.assign_location) && (
								<AgGridColumn
									field="action"
									filter={false}
									cellRenderer="handleAnnouncementActionRenderer"
									cellRendererParams={{
										_isEmployerRoute: isEmployerRoute,
										_queryGroupNumber: queryGroupNumber,
										announcementPermission: announcementPermission
									}}
								></AgGridColumn>
							)}
					</AgGridReact>
				</div>
			</div>
			{/* <div className="custom-pagination" id="custom-pagination">
				<CustomPagination pageCount={pageCount} currentPage={currentPage} handlePageChange={handlePageChange} />
			</div> */}
		</div>
	);
};

export default Announcements;
